import { LatestImageInfo } from "../scenes/engine/objects/media-repository/latest_image_info";
import { MaskInfo } from '../scenes/engine/objects/media-repository/MaskInfo';
import { Rectangle } from "@/shared/sync/mediaRepository/rectangle";
import { Size } from "@/shared/sync/mediaRepository/size";
import { Point } from "@/shared/sync/mediaRepository/point";
import { UrlHelpers } from "@/shared/services/UrlHelpers";
import { SyncManager } from "@/firebase/SyncManager";
import { HeifConverter } from "./sync/HeifConverter";
import { looksLikeHeic } from "@/utils/supportedImageFormats";

// NOTE:
// - This refactor keeps ALL method signatures the same on MediaImageRepositoryProcessing.
// - It introduces a tiny runtime abstraction that picks OffscreenCanvas when available,
//   while still returning HTMLCanvasElement objects to callers.
// - Two concrete subclasses implement DOM (fabric.js) and Offscreen (pure 2D) strategies.
// - Use `createMediaImageRepositoryProcessing()` to get the best implementation at runtime.

export enum ResizeMode {
  aspectFit,
  aspectFill,
  exactSize
}

export interface ResizeOptions {
  allowUpsampling: boolean;
  exportType: string;
  pad: boolean,
  resizeMode: ResizeMode
}

// -----------------------------
// Canvas runtime abstraction
// -----------------------------

type CanvasLike = HTMLCanvasElement | OffscreenCanvas;

export abstract class CanvasRuntime {
  abstract create(width: number, height: number): CanvasLike;
  abstract get2D(canvas: CanvasLike): CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
  abstract toBlobUrl(canvas: CanvasLike, mime: string): Promise<string>;
  abstract toDataURL(canvas: CanvasLike, mime?: string): Promise<string>;
  abstract toHTMLCanvas(canvas: CanvasLike): HTMLCanvasElement;
}

// ---------------------------------------------
// Base class (public API: method signatures kept)
// ---------------------------------------------

export abstract class MediaImageRepositoryProcessing {
  static defaultResizeOptions: ResizeOptions = {
    allowUpsampling: false,
    exportType: 'image/png',
    pad: false,
    resizeMode: ResizeMode.aspectFit
  };

  protected runtime: CanvasRuntime;
  /** Public so a caller doing its own mask work off the main thread can pass the
   *  same threshold this class would have used. */
  public maskThreshold = 10;

  constructor(runtime: CanvasRuntime) {
    this.runtime = runtime;
    // @ts-ignore
    window.mediaImageRepositoryProcessing = this;
  }

  // -------------------------
  // Helpers that stayed the same
  // -------------------------

  async aspectFitImage(image: string, frameSize: Size): Promise<{ image: string, boundingBox: Rectangle } | null> {
    const resizeOptions: ResizeOptions = {
      allowUpsampling: true,
      exportType: 'image/png',
      pad: false,
      resizeMode: ResizeMode.aspectFill
    };
    const loadedImage = await this.loadImage(image);
    const loadedImageAspectRatio = loadedImage.width / loadedImage.height;
    if (Math.abs(loadedImageAspectRatio - frameSize.aspectRatio()) < 0.01) {
      return null;
    }

    const maxSupportedEdge = 1280;
    const frameResizeRatio = Math.min(1.0, maxSupportedEdge / frameSize.maxEdge());
    const resizedFrameSize = frameSize.multiply(frameResizeRatio);

    const aspectFitImage = await this.resizeBlob(image, resizedFrameSize, null, resizeOptions);
    const imageSize = new Size(loadedImage.width, loadedImage.height);
    const initialBoundingBox = this.calculateBoundingBox(imageSize, resizedFrameSize, resizeOptions.resizeMode);
    const ratio = imageSize.maxEdge() / initialBoundingBox.maxEdge();

    const boundingBox = new Rectangle(
      initialBoundingBox.x,
      initialBoundingBox.y,
      initialBoundingBox.width - 2 * initialBoundingBox.x,
      initialBoundingBox.height - 2 * initialBoundingBox.y
    )
      .multiply(new Size(ratio, ratio))
      .multiply(new Size(1 / imageSize.width, 1 / imageSize.height));

    return { image: aspectFitImage, boundingBox };
  }

  calculateBoundingBox(inputSize: Size, targetSize: Size, resizeMode: ResizeMode): Rectangle {
    const ratio = inputSize.aspectRatio();

    let width = inputSize.width;
    let height = inputSize.height;

    if (resizeMode === ResizeMode.exactSize) {
      width = targetSize.width;
      height = targetSize.height;
    } else if (resizeMode === ResizeMode.aspectFit) {
      if (width > height) {
        width = targetSize.width;
        height = width / ratio;
      } else {
        height = targetSize.height;
        width = height * ratio;
      }
    } else if (resizeMode === ResizeMode.aspectFill) {
      const scale = Math.max(targetSize.width / inputSize.width, targetSize.height / inputSize.height);
      width = inputSize.width * scale;
      height = inputSize.height * scale;
    }

    const offsetX = resizeMode === ResizeMode.aspectFill ? Math.max(0, (width - targetSize.width)) / 2 : 0;
    const offsetY = resizeMode === ResizeMode.aspectFill ? Math.max(0, (height - targetSize.height)) / 2 : 0;

    return new Rectangle(offsetX, offsetY, width, height);
  }

  async resizeMaxEdgeSizeToCanvas(image: string, edge: number, resizeOptions: ResizeOptions = MediaImageRepositoryProcessing.defaultResizeOptions): Promise<HTMLCanvasElement> {
    const size = new Size(edge, edge);
    return await this.resizeSizeToCanvas(image, size, resizeOptions);
  }

  async resizeSizeToCanvas(image: string, size: Size, resizeOptions: ResizeOptions = MediaImageRepositoryProcessing.defaultResizeOptions): Promise<HTMLCanvasElement> {
    const htmlImg = await this.loadImage(image);
    const imageSize = new Size(htmlImg.width, htmlImg.height);
    if (!resizeOptions.allowUpsampling && Math.max(htmlImg.width, htmlImg.height) < size.maxEdge()) {
      const targetSize = new Size(htmlImg.width, htmlImg.height);
      const canvas = this.createCanvas(htmlImg, targetSize);
      return canvas;
    }

    const rect = this.calculateBoundingBox(imageSize, size, resizeOptions.resizeMode);
    const offset = new Point(rect.x, rect.y);
    const scaleX = rect.width / htmlImg.width;
    const scaleY = rect.height / htmlImg.height;
    const resizedHtmlImg = await this.resize(htmlImg, scaleX, scaleY, resizeOptions.pad);

    const targetSize = resizeOptions.resizeMode === ResizeMode.aspectFit ? new Size(resizedHtmlImg.width, resizedHtmlImg.height) : size;
    const canvas = this.createCanvas(resizedHtmlImg, targetSize, null, null, offset);
    return canvas;
  }

  async resizeBlob(image: string, size: Size, signal?: AbortSignal, resizeOptions: ResizeOptions = MediaImageRepositoryProcessing.defaultResizeOptions): Promise<string> {
    const canvas = await this.resizeSizeToCanvas(image, size, resizeOptions);
    if (signal?.aborted) { return null as unknown as string; }
    const blobUrl = await this.canvasToBlobUrl(canvas, resizeOptions.exportType);
    return blobUrl;
  }

  async resizeBlobToMaxEdgeSize(image: string, edge: number, signal?: AbortSignal, resizeOptions: ResizeOptions = MediaImageRepositoryProcessing.defaultResizeOptions): Promise<string> {
    const size = new Size(edge, edge);
    return await this.resizeBlob(image, size, signal, resizeOptions);
  }

  public async canvasToBlobUrl(canvas: HTMLCanvasElement, format: string = 'image/png'): Promise<string> {
    return this.runtime.toBlobUrl(canvas, format);
  }

  async flipImage(image: HTMLImageElement, flipX: boolean, flipY: boolean): Promise<HTMLImageElement> {
    if (!flipX && !flipY) return image;
    const canvas = this.createCanvas(image, new Size(image.width, image.height));
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.translate(
      flipX ? canvas.width : 0,
      flipY ? canvas.height : 0,
    );
    ctx.scale(
      flipX ? -1 : 1,
      flipY ? -1 : 1,
    );
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height, 0, 0, canvas.width, canvas.height);
    ctx.restore();
    return await this.loadImage(canvas.toDataURL());
  }

  async createColorImage(color: string, width: number, height: number): Promise<string> {
    const can = this.runtime.create(width, height);
    const ctx = this.runtime.get2D(can);
    (ctx as any).fillStyle = color;
    ctx.fillRect(0, 0, width, height);
    return await this.runtime.toBlobUrl(can, 'image/png');
  }

  async resizeBase64ToMaxEdgeSize(image: string, edge: number, resizeOptions: ResizeOptions = MediaImageRepositoryProcessing.defaultResizeOptions) {
    const canvas = await this.resizeMaxEdgeSizeToCanvas(image, edge, resizeOptions);
    return canvas.toDataURL(resizeOptions.exportType);
  }

  async resizeBase64ToMaxEdgeSizeAsJpeg(image: string, edge: number, quality = 0.8): Promise<string> {
    const canvas = await this.resizeMaxEdgeSizeToCanvas(image, edge, {
      ...MediaImageRepositoryProcessing.defaultResizeOptions,
      exportType: 'image/jpeg',
    });
    return canvas.toDataURL('image/jpeg', quality);
  }

  async resizeToEdge(htmlImg: HTMLImageElement, edge: number, noUpscaling: boolean = true): Promise<HTMLImageElement> {
    if (noUpscaling && (Math.max(htmlImg.width, htmlImg.height) < edge)) {
      return htmlImg;
    }
    const ratio = htmlImg.width / htmlImg.height;
    let width: number, height: number;
    if (htmlImg.width > htmlImg.height) { width = edge; height = width / ratio; }
    else { height = edge; width = height * ratio; }
    const resizedHtmlImg = await this.resize(htmlImg, width / htmlImg.width, height / htmlImg.height);
    return resizedHtmlImg;
  }

  async resizeBase64(image: string, ratioX: number, ratioY: number): Promise<string> {
    const htmlImg = await this.loadImage(image);
    const resizedHtmlImg = await this.resize(htmlImg, ratioX, ratioY);
    const targetSize = new Size(resizedHtmlImg.width, resizedHtmlImg.height);
    const canvas = this.createCanvas(resizedHtmlImg, targetSize);
    return canvas.toDataURL();
  }

  // ABSTRACT: implemented differently in DOM (fabric) vs Offscreen (pure 2D)
  abstract resize(image: HTMLImageElement, ratioX: number, ratioY: number, shouldPad?: boolean): Promise<HTMLImageElement>;

  // ABSTRACT: implemented differently in DOM (fabric) vs Offscreen (composite)
  abstract applyMaskToImage(image: HTMLImageElement, mask: HTMLImageElement, flip?: boolean): Promise<string>;

  async cropHtmlImage(image: HTMLImageElement, boundingBox: Rectangle): Promise<HTMLImageElement> {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    const that = this;
    const p = new Promise<HTMLImageElement>(async (resolve) => {
      img.onload = () => resolve(img);
      img.src = await that.doCropHtmlImage(image, boundingBox);
    });
    return p;
  }

  async padImage(image: HTMLImageElement, targetSize: Size, rect: Rectangle, bgColor: string = null): Promise<HTMLImageElement> {
    const padding = new Rectangle(rect.x, rect.y, rect.width, rect.height);
    const canvas = this.createCanvas(image, targetSize, padding, bgColor);
    const paddedImageInBase64 = canvas.toDataURL();
    const paddedImage = await this.loadImage(paddedImageInBase64);
    paddedImage.width = targetSize.width;
    paddedImage.height = targetSize.height;
    return paddedImage;
  }

  async composite(bottomImage: HTMLImageElement, topImage: HTMLImageElement, origin: Point, keepOriginalSize = false): Promise<HTMLImageElement> {
    const targetSize = new Size(bottomImage.width, bottomImage.height);
    const canvas = document.createElement('canvas');
    canvas.width = targetSize.width;
    canvas.height = targetSize.height;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(bottomImage, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    const ratioX = bottomImage.width / topImage.width;
    const ratioY = bottomImage.height / topImage.height;
    if (keepOriginalSize) {
      ctx.drawImage(topImage, origin.x * ratioX, origin.y * ratioY, bottomImage.width, bottomImage.height);
    } else {
      ctx.drawImage(topImage, origin.x / ratioX, origin.y / ratioY, bottomImage.width, bottomImage.height);
    }
    const paddedImageInBase64 = canvas.toDataURL();
    const paddedImage = await this.loadImage(paddedImageInBase64);
    paddedImage.width = targetSize.width;
    paddedImage.height = targetSize.height;
    return paddedImage;
  }

  async preprocessForBazaartBg(inputImage: string): Promise<string> {
    const minEdge = 512;
    const maxEdge = 800;
    const imageSize = await this.getImageSize(inputImage);
    const aspectRatio = imageSize.width / imageSize.height;
    const htmlImage = await this.loadImage(inputImage);

    let retHtmlImage: HTMLImageElement;
    let targetSize = new Size(0, 0);
    if (aspectRatio < 1.25 && aspectRatio > (1.0 / 1.25)) {
      targetSize = new Size(minEdge, minEdge);
      retHtmlImage = await this.resize(htmlImage, minEdge / imageSize.width, minEdge / imageSize.height);
    } else if (aspectRatio < 1) {
      targetSize = new Size(minEdge, maxEdge);
      retHtmlImage = await this.resize(htmlImage, minEdge / imageSize.width, maxEdge / imageSize.height);
    } else {
      targetSize = new Size(maxEdge, minEdge);
      retHtmlImage = await this.resize(htmlImage, maxEdge / imageSize.width, minEdge / imageSize.height);
    }

    const canvas = this.createCanvas(retHtmlImage, targetSize);
    return canvas.toDataURL();
  }

  protected base64ToUint8Array(base64: string) {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = binaryString.charCodeAt(i);
    return bytes;
  }

  public async imageElmToBlob(image: HTMLImageElement, isPNG: boolean = false, clearBackground: boolean = true): Promise<string> {
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Unable to get canvas rendering context');

    if (!clearBackground) {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    ctx.drawImage(image, 0, 0);
    return isPNG ? await this.canvasToBlobUrl(canvas, 'image/png') : await this.canvasToBlobUrl(canvas, 'image/jpeg');
  }

  async getImageSize(url: string): Promise<Size> {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    const p = new Promise<Size>((resolve) => {
      img.onload = () => resolve(new Size(img.width, img.height));
      img.src = url;
    });
    return p;
  }

  async convertToHeicToJpegIfNeeded(src: string, file?: File): Promise<string> {
    // `file` is optional: when passed and its name/MIME don't look HEIC, skip the
    // fetch()+blob() sniff in isHeic() entirely — the common (non-HEIC) upload case.
    if (file && !looksLikeHeic(file)) return src
    if (await this.isHeic(src)) {
      // Convert once, then load again and PIPE the result to resolve/reject:
      const base64 = await this.blobUrlToBase64(src);
      const jpegDataUrl = await HeifConverter.shared.convert(base64); // expect data:image/jpeg;base64,...
      return jpegDataUrl;
    } else {
      return src;
    }
  }

  async isHeic(src: string): Promise<boolean> {
    try {
      const response = await fetch(src);
      const blob = await response.blob();

      // 1. Check the MIME type (fastest and most common method)
      const heicMimeTypes = ['image/heic', 'image/heif'];
      if (heicMimeTypes.includes(blob.type)) {
        return true;
      }

      // 2. Fallback: Check magic numbers (more reliable if MIME type is wrong)
      // HEIC/HEIF files contain an "ftyp" box with a brand like 'heic', 'heix', 'mif1', etc.
      // This box is typically found within the first 12 bytes.
      if (blob.size < 12) return false;

      const buffer = await blob.slice(0, 12).arrayBuffer();
      const view = new DataView(buffer);

      // The brand identifier starts at the 8th byte (offset 8)
      const brand = String.fromCharCode(
        view.getUint8(8),
        view.getUint8(9),
        view.getUint8(10),
        view.getUint8(11)
      );

      const heicBrands = ['heic', 'heix', 'hevc', 'hevx', 'mif1'];
      return heicBrands.includes(brand);

    } catch (error) {
      console.error("Could not check image type:", error);
      return false;
    }
  }


async loadImage(str: string, retryCount = 0): Promise<HTMLImageElement | null> {
  if (retryCount > 1) return null; // at most one retry
  if (!str){
    return null;
  }
  
  return new Promise<HTMLImageElement | null>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    const cleanup = () => {
      img.onload = null;
      img.onerror = null;
      img.onabort = null;
    };

    img.onload = () => {
      cleanup();
      resolve(img);
    };

    img.onerror = async (ev: Event) => {
      try {
        if (await this.isHeic(str) && retryCount < 1) {
          // Convert once, then load again and PIPE the result to resolve/reject:
          const base64 = await this.blobUrlToBase64(str);
          const jpegDataUrl = await HeifConverter.shared.convert(base64); // expect data:image/jpeg;base64,...
          const next = await this.loadImage(jpegDataUrl, retryCount + 1);
          cleanup();
          if (next) resolve(next);
          else reject(new Error('HEIC conversion succeeded, but loading JPEG failed'));
        } else {
          cleanup();
          reject(new Error('Image failed to load'));
        }
      } catch (e) {
        cleanup();
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    };

    img.onabort = () => {
      cleanup();
      reject(new Error('Image load aborted'));
    };

    img.src = str;
  });
}


  private async doCropHtmlImage(image: HTMLImageElement, boundingBox: Rectangle): Promise<string> {
    const targetSize = new Size(boundingBox.width, boundingBox.height);
    const targetCanvas = this.createCanvas(image, targetSize, null);
    const targetCtx = targetCanvas.getContext('2d')!;
    targetCtx.clearRect(0, 0, targetSize.width, targetSize.height);

    targetCtx.drawImage(
      image,
      boundingBox.x, boundingBox.y,
      boundingBox.width, boundingBox.height,
      0, 0,
      boundingBox.width, boundingBox.height
    );

    return await this.canvasToBlobUrl(targetCanvas);
  }

  // Unified canvas creation that ALWAYS returns an HTMLCanvasElement to the caller
  protected createCanvas(img: HTMLImageElement, size: Size, padding?: Rectangle, bgColor?: string | null, offset: Point = Point.zero()): HTMLCanvasElement {
    const can = this.runtime.create(size.width, size.height);
    const ctx = this.runtime.get2D(can);

    if (bgColor) {
      (ctx as any).fillStyle = bgColor;
      ctx.fillRect(0, 0, size.width, size.height);
    }

    if (padding) {
      ctx.drawImage(img as CanvasImageSource, padding.x + offset.x, padding.y + offset.y, padding.width, padding.height);
    } else {
      ctx.drawImage(img as CanvasImageSource, offset.x, offset.y, size.width, size.height, 0, 0, size.width, size.height);
    }

    // Convert to an HTMLCanvasElement no matter what backend we used
    // (DOM runtime will just return the original canvas)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (this.runtime as any).toHTMLCanvas(can);
  }

  public async base64ToBlobUrl(base64: string, isPNG = false): Promise<string> {
    return UrlHelpers.base64ToBlobUrl(base64, isPNG);
  }

  public blobUrlToBase64(blobUrl: string | URL): Promise<string> {
    return UrlHelpers.blobUrlToBase64(blobUrl);
  }

  public dataURIToBlob(dataURI: string, isPNG = false) {
    return UrlHelpers.dataURIToBlob(dataURI, isPNG);
  }

  async createdJpgSquareImageWithPadding(base64Image: string, edgeSize: number): Promise<any> {
    let htmlImage = await this.loadImage(base64Image)
    const paddedImage = await this.resizeBase64ToMaxEdgeSize(base64Image, edgeSize, {
      allowUpsampling: true,
      exportType:'image/jpeg',
      pad: true,
      resizeMode: ResizeMode.aspectFit
    });
    const cropPadData = this.calculateImageDimensions(htmlImage, edgeSize);
    let result = { paddedImage, cropPadData }
    return result
  }

  public calculateImageDimensions(htmlImage: any, edgeSize: number) {
    const aspectRatio = htmlImage.width / htmlImage.height
    let paddedCrop: Rectangle;
    
    if (htmlImage.width > htmlImage.height) {
      const newHeight = edgeSize / aspectRatio
      const y = (edgeSize - newHeight) / 2
      paddedCrop = new Rectangle(0, y, edgeSize, newHeight);
    } else {
      const newWidth = edgeSize * aspectRatio
      const x = (edgeSize - newWidth) / 2
      paddedCrop = new Rectangle(x, 0, newWidth, edgeSize);
    }
    return paddedCrop
  }
}
